// ==================== ENGAGEMENT METRICS COMPONENT ====================

import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Eye, 
  MessageCircle, 
  ArrowDownLeft,
  ArrowUpRight,
  Users,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { cn } from '../../../utils/helpers';
import { staggerItem } from '../../../utils/animations';
import type { EngagementMetrics as EngagementMetricsType } from '../../../services/customer-dashboard.service';

// ==================== TYPES ====================

interface EngagementMetricsProps {
  data: EngagementMetricsType | null;
  isLoading?: boolean;
}

// ==================== METRIC CARD ====================

const MetricCard = ({
  label,
  value,
  icon: Icon,
  color,
  suffix = '',
}: {
  label: string;
  value: number | string;
  icon: typeof TrendingUp;
  color: string;
  suffix?: string;
}) => (
  <div className="flex items-center gap-2 p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
    <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', color)}>
      <Icon size={14} className="text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] text-(--color-muted-gray) truncate">{label}</p>
      <p className="text-sm font-bold text-white">
        {value}{suffix}
      </p>
    </div>
  </div>
);

// ==================== COMPONENT ====================

export const EngagementMetrics = ({ data, isLoading = false }: EngagementMetricsProps) => {
  if (isLoading) {
    return (
      <motion.div variants={staggerItem}>
        <Card>
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base flex items-center gap-1.5">
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-green-500" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp Engagement
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="grid grid-cols-2 gap-2 animate-pulse">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-14 bg-(--color-border-gray) rounded" />
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div variants={staggerItem}>
      <Card>
        <CardHeader className="border-b-0 pb-1 mb-0">
          <CardTitle className="text-base flex items-center gap-1.5">
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-green-500" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp Engagement
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="space-y-3">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 gap-2">
              <MetricCard
                label="Avg Delivery Rate"
                value={data?.avgDeliveryRate || 0}
                suffix="%"
                icon={TrendingUp}
                color="bg-green-500"
              />
              <MetricCard
                label="Avg Read Rate"
                value={data?.avgReadRate || 0}
                suffix="%"
                icon={Eye}
                color="bg-blue-500"
              />
              <MetricCard
                label="Total Conversations"
                value={data?.totalConversations || 0}
                icon={MessageCircle}
                color="bg-purple-500"
              />
              <MetricCard
                label="Active (24h)"
                value={data?.activeConversations || 0}
                icon={Users}
                color="bg-(--color-signal-orange)"
              />
            </div>

            {/* Message Direction */}
            <div className="p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
              <p className="text-[10px] text-(--color-muted-gray) mb-2">Message Direction</p>
              <div className="flex items-center gap-2">
                {/* Progress bar */}
                <div className="flex-1 h-2 bg-(--color-border-gray) rounded-full overflow-hidden flex">
                  <div 
                    className="h-full bg-blue-500 transition-all duration-500"
                    style={{ width: `${data?.messageDirectionStats.outboundPercentage || 50}%` }}
                  />
                  <div 
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${data?.messageDirectionStats.inboundPercentage || 50}%` }}
                  />
                </div>
              </div>
              <div className="flex justify-between mt-1.5 text-[10px]">
                <div className="flex items-center gap-1">
                  <ArrowUpRight size={10} className="text-blue-400" />
                  <span className="text-(--color-soft-gray)">
                    Outbound: {data?.messageDirectionStats.outbound || 0} ({data?.messageDirectionStats.outboundPercentage || 0}%)
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <ArrowDownLeft size={10} className="text-green-400" />
                  <span className="text-(--color-soft-gray)">
                    Inbound: {data?.messageDirectionStats.inbound || 0} ({data?.messageDirectionStats.inboundPercentage || 0}%)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default EngagementMetrics;
